import MethodsGrid from '@/components/MethodsGrid/GridWrapper';
import GridItem from '@/components/MethodsGrid/GridItem';
import MethodsSkeleton from '@/components/Skeletons/PaymentMethodsSkeleton';

import { mapActions, mapGetters, mapState } from 'vuex';

import f_maybe from '@/helpers/maybe';
import pc_f_compose from '@/helpers/compose';
import Tooltip from '@/components/UI/Tooltip';
import ExtHppIcon from '@/components/svg/ExternalHPP';

import Popper from 'vue-popperjs';

import styled from 'vue-styled-components';

// TODO: make external component with styles

const StyledEmptyStub = styled.section`
  color: var(--on_background);
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTooltip = styled('div', {})`
  background: var(--secondary);
  color: var(--on_secondary);
  padding: 15px;
  line-height: 1.33;
  font-size: 12px;
  width: 280px;
  padding: 15px;
  text-align: left;
  z-index: 2;
  border-radius: 4px;
  .popper__arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
  }
  &[x-placement^='right-start'] {
    margin-top: 5px;
    .popper__arrow {
      background-color: var(--secondary);
      border-width: 0;
      top: 15px;
      width: 10px;
      height: 10px;
      left: -10px;
      margin-top: 0;
      margin-bottom: 0;
      transform: rotate(45deg);
    }
  }
  &[x-placement^='left-start'] {
    margin-top: 5px;
    .popper__arrow {
      background-color: var(--secondary);
      border-width: 0;
      top: 15px;
      width: 10px;
      height: 10px;
      right: -10px;
      margin-top: 0;
      margin-bottom: 0;
      transform: rotate(45deg);
    }
  }
  &[x-placement^='bottom'] {
    margin-top: 5px;
    .popper__arrow {
      background-color: var(--secondary);
      border-width: 0;
      right: 15px;
      width: 10px;
      height: 10px;
      top: 15px;
      margin-top: 0;
      margin-bottom: 0;
      transform: rotate(45deg);
    }
  }
  &[x-placement^='top'] {
    margin-top: 5px;
    .popper__arrow {
      background-color: var(--secondary);
      border-width: 0;
      right: 15px;
      width: 10px;
      height: 10px;
      bottom: 15px;
      margin-top: 0;
      margin-bottom: 0;
      transform: rotate(45deg);
    }
  }
`;

const StyledExternalHppIcon = styled(ExtHppIcon, {})`
  path {
    &:nth-child(2) {
      fill: var(--primary);
    }
  }
`;

const StyledSvgIconWrap = styled('div', {
  tooltipColor: String,
})`
  line-height: 1;
  padding: 3px 5px 0 4px;
  margin-bottom: 4px;
  border: solid 1px
    ${props => (props.tooltipColor == 'primary' ? `var(--primary)` : `var(--success)`)};
  border-radius: var(--border_radius);
`;

export default {
  name: 'PaymentMethodsList',
  components: [
    Tooltip,
    ExtHppIcon,
    Popper,
    StyledExternalHppIcon,
    StyledSvgIconWrap,
    StyledEmptyStub,
  ],

  render() {
    return this.isApplicationReady
      ? !this.paymentMethods.length
        ? this.renderEmptyStub()
        : this.renderMethodsList()
      : this.renderSkeleton();
  },
  created() {
    // If data was already loaded and app initialized, check for available sevices
    if (this.isApplicationReady) {
      this.setNavigationByCategories(this.categories);

      // If no abailable services / methods left -> redirect on the error page;
      if (!this.calculateAvailableServices()) {
        this.setGlobalError({
          status: 'no_availables_services',
          code: 'no_availables_services',
          status_code: 'no_availables_services',
        });
      }
    }
  },

  methods: {
    ...mapActions(['selectMethod', 'setTabsNavigationData', 'setGlobalError']),

    _handleMethodSelect(method) {
      this.selectMethod(method);
      this.$router.push(this.getRoute(method));
    },

    markAutoProcess(method) {
      // If only 1 service, and flow hpp this service might be autoprocessed;

      const services = this.services[method.code] || [];
      if (services && services.length && services.length == 1) {
        if (services[0].flow === 'hpp' && services[0].fields && services[0].fields.length === 0) {
          method.autorocess = true;
          method.tooltip = true;
          method.tooltipData = {
            title: 'direct_call_forwarding',
            description: 'direct_call_forwarding_description',
          };
        }
      }

      return { ...method };
    },

    /**
     *  @returns {Array} array of filtered method names based on search string
     */
    filterByValue(arr) {
      return arr.filter(n => n.toLowerCase().indexOf(this.searchData.toLowerCase()) !== -1);
    },

    convertToUIFormat({
      name = '',
      code = ' ',
      logo = '',
      icon = null,
      tooltip = false,
      tooltipData = {},
    }) {
      return {
        name,
        code,
        // Good night, ESLINT
        image:
          (this.customThemeOptions || {}).show_method_logo &&
          (this.customThemeOptions || {}).show_method_logo.toLowerCase() === 'true'
            ? logo
            : icon
            ? icon
            : logo,

        tooltip,
        tooltipData,
      };
    },

    // TODO: Throw an error if no categories passed, or not an array passed.

    setNavigationByCategories(categories) {
      const newCategories = categories || [];

      const navigationData = newCategories.map(el => {
        let newEl = {};
        newEl.callback = 'category';
        newEl.name = el;
        return newEl;
      });
      this.setTabsNavigationData(navigationData);
    },

    /**
     *  Checks is any method has available service;
     * @returns {boolean}
     */
    calculateAvailableServices() {
      return this.paymentMethods.some(method => {
        return !this.isInDisabledMethods(method.code);
      });
    },

    // /**
    //  *
    //  * @param state
    //  * @returns {function(*=): boolean}
    //  */
    // isInDisabledMethods: state => methodCode => {
    //     return state.disabled_methods.indexOf(methodCode) != -1;
    // },

    /**
     * Just JSX template
     * @returns {boolean}
     */
    renderTooltip(method) {
      const popoverOptions = {
        modifiers: {
          arrow: { enabled: true, element: 'popper__arrow' },
        },
      };
      if (!this.isMobile) {
        popoverOptions.placement = 'right-start';
        popoverOptions.modifiers.offset = '-15,15,0,0px';
      }

      const tooltipColor = 'primary';

      return (
        <div class="payment-methods__icon-wrap">
          <StyledSvgIconWrap tooltipColor={tooltipColor}>
            <Popper trigger={'hover'} options={popoverOptions}>
              <StyledTooltip class="poper">
                <div class="tooltip__title">{this.$t(method.tooltipData.title)}</div>
                <div class="tooltip__description">{this.$t(method.tooltipData.description)}</div>
              </StyledTooltip>
              <div slot={'reference'}>
                <StyledExternalHppIcon />
              </div>
            </Popper>
          </StyledSvgIconWrap>
        </div>
      );
    },

    renderEmptyStub() {
      return <StyledEmptyStub>{this.$t('empty_data')}</StyledEmptyStub>;
    },

    renderMethodsList() {
      return (
        <MethodsGrid>
          {this.paymentMethods.map((method, index) => {
            return (
              <MethodsGrid.Item
                class="payment-methods__item"
                disabled={this.isInDisabledMethods(method.code)}
                size={this.itemSize}
                nativeOn={{
                  click: e => {
                    this._handleMethodSelect(method);
                  },
                }}
                key={index}
              >
                <MethodsGrid.Item.overlay class="grid_item__overlay"></MethodsGrid.Item.overlay>
                <GridItem.logoWrap>
                  {method.tooltip ? this.renderTooltip(method) : null}
                  <GridItem.logo imageSrc={method.image}></GridItem.logo>
                </GridItem.logoWrap>
                <MethodsGrid.Item.Title class="grid_item__title">
                  {method.name[(this.$i18n || {}).locale || 'en'] ||
                    method.name['en'] ||
                    method.code}
                </MethodsGrid.Item.Title>
              </MethodsGrid.Item>
            );
          })}
        </MethodsGrid>
      );
    },

    renderSkeleton() {
      let skeletonItems = [];
      for (let i = 0; i < 12; i++) {
        skeletonItems.push(
          <MethodsSkeleton.Item size={this.itemSize} class="post method payment-methods__item">
            <div class="avatar icon" />
            <div class="line name" />
          </MethodsSkeleton.Item>
        );
      }

      return <MethodsSkeleton>{skeletonItems}</MethodsSkeleton>;
    },

    getRoute(method) {
      let route = {
        name: 'pm',
        params: { pm: method.code },
        query: { 'payment-request': this.requestId },
      };

      return route;
    },
  },

  computed: {
    ...mapGetters(['isInDisabledMethods', 'getMethodsByCategoryName']),
    ...mapState([
      'services',
      'methods',
      'categories',
      'selectedCategory',
      'groupedCategories',
      'searchData',
      'isApplicationReady',
    ]),

    // TODO: make store architecure more clear. More getters, less shit like below;
    /**
     *  @returns Methods grouped by category
     */
    currentMethods() {
      return this.getMethodsByCategoryName(this.selectedCategory);
    },
    paymentMethods() {
      let methodsToShow = f_maybe(Object.values(this.currentMethods))(m =>
        m.map(pc_f_compose(this.markAutoProcess, this.convertToUIFormat))
      ).f_result();

      if (this.searchData) {
        /**
         * https://lodash.com/docs/4.17.11#compact
         * Creates an array with all falsey values removed.
         * The values false, null, 0, "", undefined, and NaN are falsey.
         */
        methodsToShow = _.compact(
          methodsToShow.map(m => {
            if (f_maybe(Object.values(m.name))(this.filterByValue).f_result().length) {
              return m;
            }
          })
        );
      }
      return methodsToShow;
    },
    itemSize() {
      let size = '183.3px';
      if (this.screenSize.width > 1050 && this.screenSize.width <= 1240) {
        size = `${(this.screenSize.width - 140) / 6}px`;
      } else if (this.screenSize.width > 850 && this.screenSize.width <= 1050) {
        size = `${(this.screenSize.width - 120) / 5}px`;
      } else if (this.screenSize.width > 733 && this.screenSize.width <= 850) {
        size = `${(this.screenSize.width - 100) / 4}px`;
      } else if (this.screenSize.width > 557 && this.screenSize.width <= 734) {
        size = `${(this.screenSize.width - 80) / 3}px`;
      } else if (this.screenSize.width <= 557) {
        size = `${(this.screenSize.width - 60) / 2}px`;
      }

      return size;
    },
  },
};
